import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { FaExclamationTriangle } from "react-icons/fa";
import TermosApi, { PendenciasCarteira } from "../../services/termosApi";

const PADRAO = "Entre em contato com a central para regularização dos termos de aceite";

/**
 * Aviso de termo pendente na carteira do supervisor.
 *
 * O supervisor não é barrado no login — ele não tem como resolver nada sozinho, e travar
 * a autenticação dele por pendência de terceiro seria injusto. Mas, uma vez dentro, o
 * portal fica bloqueado até ele acionar a central: o objetivo é que a pendência do
 * cliente seja tratada, não que fique num canto da tela sendo ignorada.
 *
 * Não há botão de aceitar nem de recusar, e nunca haverá: quem aceita termo é o cliente
 * master do estabelecimento. Aqui só se enxerga quem está travado e por quê.
 */
export default function AvisoCarteira() {
  const [dados, setDados] = useState<PendenciasCarteira | null>(null);

  const carregar = useCallback(async () => {
    try {
      setDados(await TermosApi.getPendenciasCarteira());
    } catch (error) {
      // pendência de termo não pode derrubar o portal do supervisor
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  function sair() {
    window.localStorage.clear();
    window.location.href = "/login";
  }

  if (!dados || dados.clientes === 0) return <></>;

  return (
    <Modal
      isOpen
      onClose={() => {}}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      isCentered
      size="4xl"
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.800" />
      <ModalContent>
        <ModalBody py={8} px={8}>
          <Flex direction="column" align="center" textAlign="center" gap={3} mb={5}>
            <Icon as={FaExclamationTriangle} boxSize={10} color="orange.400" />

            <Text fontSize="xl" fontWeight="bold">
              {dados.clientes === 1
                ? "1 cliente da sua carteira está com termo pendente"
                : `${dados.clientes} clientes da sua carteira estão com termo pendente`}
            </Text>

            <Text fontSize="md" color="gray.700">
              {dados.mensagem || PADRAO}
            </Text>

            <Text fontSize="sm" color="gray.500">
              O aceite é feito pelo próprio estabelecimento, no portal do cliente. Você não
              consegue aceitar nem recusar por ele — acione a central para regularizar.
            </Text>
          </Flex>

          <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflowX="auto">
            <Table size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Cliente</Th>
                  <Th>CNPJ / CPF</Th>
                  <Th>Termo</Th>
                  <Th>Motivo</Th>
                  <Th>Desde</Th>
                </Tr>
              </Thead>
              <Tbody>
                {dados.itens.map((p) => (
                  <Tr key={`${p.idCliente}-${p.termo}-${p.versao}`}>
                    <Td fontSize="xs">
                      <Text fontWeight="semibold">{p.cliente}</Text>
                      <Text color="gray.500">#{p.idCliente}</Text>
                    </Td>
                    <Td fontSize="xs">{p.documento || "—"}</Td>
                    <Td fontSize="xs">
                      {p.termo}
                      <Text color="gray.500">v{p.versao}</Text>
                    </Td>
                    <Td fontSize="xs">
                      <Badge colorScheme={p.motivo === "RECUSADO" ? "red" : "orange"}>
                        {p.motivo === "RECUSADO" ? "Recusado" : "Prazo vencido"}
                      </Badge>
                    </Td>
                    <Td fontSize="xs">
                      {moment(p.prazoEfetivo).format("DD/MM/YY")}
                      {p.diasEmAtraso > 0 && (
                        <Text color="red.600">{p.diasEmAtraso} dia(s)</Text>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          <Flex justify="center" mt={5}>
            <Button colorScheme="gray" onClick={sair}>
              Sair
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
